<template>
  <card-dialog
    v-model="modalVisible"
    fullscreen
    persistent
    prevent-close
    @close="anyProgressMade ? closeConfirmationDialogVisible = true : modalVisible = false"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="protocol">
          {{ $t("card_dialog.edit_diagnostic_protocol.title") }}
        </div>
        <div v-else>
          {{ $t('new_diagnostic_protocols.page.header') }}
        </div>
      </v-card-title>
    </template>
    <v-card-text class="pa-0">
      <validation-observer ref="validation">
        <v-row
          class="fill-height"
          no-gutters
        >
          <v-col
            cols="4"
            class="fill-height overflow-auto pa-6"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="diagnosticProtocolFormData.name"
                :error-messages="errors"
                :label="$t('text_input.protocol_name.title')"
                outlined
                dense
                :placeholder="$t('text_input.protocol_name.placeholder')"
              />
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-textarea
                v-model="diagnosticProtocolFormData.description"
                :error-messages="errors"
                :label="$t('training_builder.text_input.description.title')"
                outlined
                :rows="3"
                :placeholder="$t('text_input.diagnostic_protocol.description.placeholder')"
              />
            </validation-provider>
            <v-card
              v-if="selectedVariables.length === 0"
              color="grey lighten-4"
              rounded="lg"
              flat
            >
              <v-card-text class="text-center py-6 grey--text">
                <div class="mb-4">
                  <v-icon
                    large
                    color="grey lighten-1"
                  >
                    fal fa-circle-plus
                  </v-icon>
                </div>
                <div>{{ $t("diagnostics.add_variables_box.placeholder") }}</div>
              </v-card-text>
            </v-card>
            <draggable
              class="mt-2"
              :list="selectedVariables"
              handle=".handle"
              :group="{ name: '' }"
              :invert-swap="true"
              :swap-treshold="0.6"
              :animation="100"
            >
              <div
                v-for="(testGroup, index) in selectedVariables"
                :key="index"
              >
                <v-hover v-slot="{hover}">
                  <v-card
                    class="mt-2"
                    outlined
                    rounded="lg"
                  >
                    <v-card-text class="pa-3">
                      <v-row
                        align="center"
                        no-gutters
                      >
                        <v-col cols="auto">
                          <v-icon
                            small
                            class="handle"
                            style="cursor: move"
                          >
                            far fa-grip-lines
                          </v-icon>
                        </v-col>
                        <v-col class="ml-2">
                          <div class="font-weight-medium text-subtitle-2">
                            {{ testGroup.test_name }}
                          </div>
                        </v-col>
                        <v-col
                          cols="auto"
                        >
                          <v-btn
                            :style="{ opacity: hover ? 1 : 0.2, transition: 'opacity 0.3s' }"
                            icon
                            small
                            @click="selectedVariables.splice(index, 1)"
                          >
                            <v-icon
                              x-small
                              color="error"
                            >
                              far fa-trash-alt
                            </v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <draggable
                        class="pa-2"
                        :list="testGroup.variables"
                        handle=".handle-variable"
                        :group="{ name: 'variables' + index }"
                        :invert-swap="true"
                        :swap-treshold="0.6"
                        :animation="100"
                      >
                        <div
                          v-for="(variable, variableIndex) in testGroup.variables"
                          :key="variableIndex"
                          class="grey lighten-4 ma-1 pa-2 rounded text-caption"
                        >
                          <v-hover v-slot="{hover: innerHover}">
                            <v-row
                              no-gutters
                              align="center"
                            >
                              <v-col cols="auto">
                                <v-icon
                                  small
                                  class="handle-variable mr-2"
                                  style="cursor: move"
                                >
                                  far fa-grip-lines
                                </v-icon>
                              </v-col>
                              <v-col>
                                <strong style="display: inline">{{ variable.var }}</strong>
                                <br>
                                <span>{{ variable.input_method_code === 'M' ? $t('diagnostics.input_methods.manual') : (variable.input_method_code === 'AM' ? $t('diagnostics.input_methods.automatic_manual') : $t('diagnostics.input_methods.automatic')) }}</span>
                                <span class="px-1">&middot;</span>
                                <span><strong>{{ variable.mes_rep }}</strong> reps</span>
                                <span class="px-1">&middot;</span>
                                <span><strong>Unit:</strong> {{ variable.unit }}</span>
                              </v-col>
                              <v-col
                                cols="auto"
                              >
                                <v-btn
                                  :style="{ opacity: innerHover ? 1 : 0, transition: 'opacity 0.3s' }"
                                  small
                                  icon
                                  @click="selectVariable({item: variable, value: false}, testGroup)"
                                >
                                  <v-icon
                                    x-small
                                    color="error"
                                  >
                                    far fa-trash-alt
                                  </v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-hover>
                        </div>
                      </draggable>
                    </v-card-text>
                  </v-card>
                </v-hover>
              </div>
            </draggable>
          </v-col>
          <v-divider vertical />
          <v-col class="fill-height pa-6 pb-0">
            <div class="d-flex flex-column no-gutters fill-height">
              <div class="col col-auto pa-0">
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col>
                    <subtitle>
                      {{ $t("diagnostics.diagnostic_variables.title") }}
                    </subtitle>
                  </v-col>
                  <v-col cols="auto">
                    <v-text-field
                      v-model="diagnosticTests.filters.name"
                      dense
                      outlined
                      hide-details
                      append-icon="fal fa-search"
                      :placeholder="$t('text_input.tests_included.placeholder')"
                      clearable
                      style="width: 250px"
                    />
                  </v-col>
                  <v-col
                    cols="auto"
                    class="ml-2"
                  >
                    <v-select
                      v-model="diagnosticTests.filters.group_id"
                      :items="sortBy(diagnosticGroups.data, 'name')"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      clearable
                      hide-details
                      :placeholder="$t('dropdown.group_name.placeholder')"
                      style="width: 250px"
                    />
                  </v-col>
                </v-row>
                <div
                  v-if="diagnosticTests.pagination && diagnosticTests.pagination.last_page && diagnosticTests.pagination.last_page > 1"
                  class="d-flex justify-end align-center py-2"
                >
                  <v-pagination
                    v-model="diagnosticTests.pagination.current_page"
                    :length="diagnosticTests.pagination.last_page"
                    :total-visible="6"
                  />
                </div>
              </div>
              <div
                class="col overflow-auto pa-0 mt-2 rounded"
              >
                <v-data-table
                  class="tests-table"
                  :items="diagnosticTests.data"
                  :headers="[ { value: 'test_name', text: 'Test Name'}, { value: 'group.name', text: $t('table_headers.group'), width: '30%'}, {value: 'data-table-expand'}]"
                  :loading="diagnosticTests.loading"
                  checkbox-color="primary"
                  hide-default-footer
                  disable-pagination
                  disable-sort
                  show-expand
                  dense
                  :expanded.sync="expanded"
                >
                  <template v-slot:item="{item, isExpanded}">
                    <tr @click="handleRowClick(item, isExpanded)">
                      <td>
                        {{ item.test_name }}
                        <v-tooltip
                          v-if="item.dependent_variables"
                          top
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              class="ml-2"
                              color="primary"
                              v-on="on"
                            >
                              fal fa-info-circle
                            </v-icon>
                          </template>
                          <div>
                            <span>In order to get the most of this test, you need to include the following variables:</span>
                            <ul>
                              <li
                                v-for="(variable, index) in item.dependent_variables"
                                :key="index"
                                class="pa-0"
                              >
                                {{ variable.var_name }}
                              </li>
                            </ul>
                          </div>
                        </v-tooltip>
                      </td>
                      <td>
                        <div class="d-flex justify-space-between align-center">
                          <span>
                            {{ item.group.name }}
                          </span>
                          <span v-if="item.is_vald">
                            <img
                              src="@/assets/img/vald_logo.png"
                              style="width: 40px; object-fit: contain"
                            >
                          </span>
                        </div>
                      </td>
                      <td>
                        <v-icon>
                          {{ expanded.includes(item) ? 'fal fa-chevron-up' : 'fal fa-chevron-down' }}
                        </v-icon>
                      </td>
                    </tr>
                  </template>
                  <template v-slot:expanded-item="{ item }">
                    <td
                      colspan="4"
                      class="pa-0"
                    >
                      <v-data-table
                        :headers="[ { value: 'var', text: $t('table_headers.name')}, { value: 'input_method_code', text: $t('table_headers.input'), width: '25%'}, { value: 'unit', text: $t('table_headers.unit'), width: '10%'}]"
                        :items="item.variables"
                        item-value="id"
                        show-select
                        hide-default-footer
                        fixed-header
                        dense
                        disable-pagination
                        sort-by="var"
                        :value="selectedVariablesFlatten"
                        checkbox-color="primary"
                        @click:row="handleVariableClick($event, item)"
                        @item-selected="selectVariable($event, item)"
                        @toggle-select-all="selectAllVariablesInTest($event, item)"
                      >
                        <template v-slot:item.input_method_code="{ item: variable }">
                          <td>
                            {{ variable.input_method_code === 'M' ? $t('diagnostics.input_methods.manual') :
                              (variable.input_method_code === 'AM' ? $t('diagnostics.input_methods.automatic_manual') :
                                $t('diagnostics.input_methods.automatic')) }}
                          </td>
                        </template>
                      </v-data-table>
                    </td>
                  </template>
                </v-data-table>
              </div>
            </div>
          </v-col>
        </v-row>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          color="primary"
          large
          :loading="submitting"
          @click="submit()"
        >
          {{ $t('training_builder.button.save') }}
        </v-btn>
        <v-btn
          color="grey"
          text
          @click="modalVisible = false"
        >
          {{ $t('button.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="diagnosticProtocolFormData.id"
          color="error"
          text
          @click="deleteConfirmationDialogVisible = true"
        >
          {{ $t('button.delete') }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteProtocol()"
    />

    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="modalVisible = false"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { DiagnosticProtocolApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import Subtitle from '@/components/helpers/Subtitle'
import { cloneDeep, groupBy, sortBy } from 'lodash'
import { DiagnosticGroupsApiService } from '@/data/services/DiagnosticGroupsApiService'
import debounce from 'lodash/debounce'
import draggable from 'vuedraggable'
import { DiagnosticTestApiService } from '@/data/services/DiagnosticTestApiService'

const emptyDiagnosticProtocolFormData = {
  id: undefined,
  name: '',
  description: ''
}

export default {
  name: 'DiagnosticProtocolFormDialog',
  components: { draggable, Subtitle, DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    protocol: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      diagnosticProtocolFormData: cloneDeep(emptyDiagnosticProtocolFormData),
      submitting: false,
      diagnosticTests: {
        loading: false,
        data: [],
        pagination: {
          page: 1,
          per_page: 10,
          current_page: 1,
          last_page: undefined
        },
        filters: {
          name: '',
          group_id: null
        }
      },
      diagnosticGroups: {
        loading: false,
        data: []
      },
      selectedVariables: [],
      expanded: [],
      anyProgressMade: false,
      deleteConfirmationDialogVisible: false,
      closeConfirmationDialogVisible: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedVariablesFlatten() {
      return this.selectedVariables
        .map(group => group.variables)
        .flat(1)
        .filter((item, index, self) =>
          index === self.findIndex(i => i.id === item.id)
        )
    }
  },
  watch: {
    visible(newValue) {
      if (!newValue) return

      this.diagnosticTests.filters = {
        name: '',
        test_name: undefined,
        group_id: null
      }

      this.diagnosticTests.pagination.current_page = 1

      this.fetchDiagnosticGroups()
      this.fetchDiagnosticTests()

      setTimeout(() => {
        this.$refs.validation.reset()
      }, 0)

      this.$nextTick(() => {
        this.anyProgressMade = false
      })
    },
    'diagnosticTests.filters': {
      deep: true,
      handler() {
        if (this.diagnosticTests.pagination.current_page === 1) {
          this.fetchDiagnosticTestsDebounced()
        } else {
          this.diagnosticTests.pagination.current_page = 1
        }
      }
    },
    'diagnosticTests.pagination.current_page': function () {
      this.fetchDiagnosticTestsDebounced()
    },
    selectedVariables: {
      deep: true,
      handler() {
        this.anyProgressMade = true
      }
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_PROTOCOL_FORM, () => {
      this.modalVisible = true
      this.diagnosticProtocolFormData = cloneDeep(emptyDiagnosticProtocolFormData)
      this.selectedVariables = []
    })

    EventBus.$on(this.$events.EDIT_PROTOCOL, (protocol) => {
      this.diagnosticProtocolFormData.id = protocol.id
      this.diagnosticProtocolFormData.name = protocol.name
      this.diagnosticProtocolFormData.description = protocol.description

      this.selectedVariables = Object.entries(groupBy(protocol.variables, 'test.test_name'))
        .map(([test_name, variables]) => ({
          test_name,
          variables
        }))

      this.modalVisible = true
    })
  },
  methods: {
    sortBy,
    fetchDiagnosticTestsDebounced: debounce(function () {
      this.fetchDiagnosticTests()
    }, 250),
    async fetchDiagnosticGroups() {
      this.diagnosticGroups.loading = true

      try {
        const result = await DiagnosticGroupsApiService.getAll()

        this.diagnosticGroups.data = result.data.map(group => ({
          ...group,
          name: this.$t(`diagnostics.groups.${group.name}`)
        }))
      } catch (e) {

      } finally {
        this.diagnosticGroups.loading = false
      }
    },
    async fetchDiagnosticTests() {
      this.diagnosticTests.loading = true

      try {
        const data = {
          name: this.diagnosticTests.filters.name,
          group_id: this.diagnosticTests.filters.group_id,
          page: this.diagnosticTests.pagination.current_page,
          per_page: this.diagnosticTests.pagination.per_page
        }

        const result = await DiagnosticTestApiService.getAll(data)

        this.diagnosticTests.data = result.data.map(test => ({
          ...test,
          group: {
            ...test.group.id,
            name: this.$t(`diagnostics.groups.${test.group.name}`)
          }
        }))

        this.diagnosticTests.pagination = result.meta
      } catch (e) {

      } finally {
        this.diagnosticTests.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.diagnosticProtocolFormData.name,
          description: this.diagnosticProtocolFormData.description,
          variables: this.selectedVariables.map(group => group.variables).flat(1).map((variable, index) => {
            return {
              id: variable.id,
              order: index + 1
            }
          })
        }

        let result

        if (this.diagnosticProtocolFormData.id) {
          data.id = this.diagnosticProtocolFormData.id

          result = await DiagnosticProtocolApiService.update(data)
        } else {
          result = await DiagnosticProtocolApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.diagnostic_protocol_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.diagnosticProtocolFormData.id) {
          EventBus.$emit(this.$events.PROTOCOL_UPDATED, result.data)
        } else {
          EventBus.$emit(this.$events.PROTOCOL_CREATED, result.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.diagnostic_protocol_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteProtocol() {
      if (!this.diagnosticProtocolFormData.id) return

      try {
        this.deleting = true

        await DiagnosticProtocolApiService.delete(this.diagnosticProtocolFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.protocol_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.PROTOCOL_DELETED, this.diagnosticProtocolFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.protocol_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    selectVariable(selectedObject, test) {
      const testGroup = this.selectedVariables.find(group => group.test_name === test.test_name)

      if (testGroup) {
        if (selectedObject.value) {
          testGroup.variables.push(selectedObject.item)
        } else {
          testGroup.variables = testGroup.variables.filter(variable => variable.id !== selectedObject.item.id)
          if (testGroup.variables.length === 0) {
            this.selectedVariables = this.selectedVariables.filter(group => group.test_name !== test.test_name)
          }
        }
      } else {
        if (selectedObject.value) {
          this.selectedVariables.push({
            test_name: test.test_name,
            variables: [selectedObject.item]
          })
        }
      }
    },
    selectAllVariablesInTest(selectedObject, test) {
      const testGroup = this.selectedVariables.find(group => group.test_name === test.test_name)

      if (testGroup) {
        if (selectedObject.value) {
          testGroup.variables = test.variables
        } else {
          this.selectedVariables = this.selectedVariables.filter(group => group.test_name !== test.test_name)
        }
      } else {
        if (selectedObject.value) {
          this.selectedVariables.push({
            test_name: test.test_name,
            variables: test.variables
          })
        }
      }
    },
    handleRowClick(item, isExpanded) {
      if (!isExpanded) {
        this.expanded.push(item)
      } else {
        this.expanded = this.expanded.filter(t => t.id !== item.id)
      }
    },
    handleVariableClick(item, test) {
      const testGroup = this.selectedVariables.find(group => group.test_name === test.test_name)

      if (testGroup && testGroup.variables.find(variable => variable.id === item.id)) {
        this.selectVariable({ item, value: false }, test)
      } else if (testGroup && !testGroup.variables.find(variable => variable.id === item.id)) {
        this.selectVariable({ item, value: true }, test)
      } else {
        this.selectVariable({ item, value: true }, test)
      }
    }
  }
}
</script>

<style>
.tests-table > .v-data-table__wrapper > table > tbody > tr {
  background-color: #f6f6f6;
}

.tests-table > .v-data-table__wrapper > table > tbody > tr:hover {
  cursor: pointer;
}

.v-data-table > .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none !important;
}

</style>
